import { Loading } from 'authentication-page';
import { noop } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useState, type FC } from 'react';
import type { AnalysisContextValue, FactorLensesContextValue } from 'venn-components';
import {
  AnalysisContext,
  AnalysisExportStore,
  FactorLensesContext,
  PortfoliosContext,
  UserContext,
} from 'venn-components';

import { useSetRecoilState } from 'recoil';
import { ThemeContext } from 'styled-components';
import { getAllFactorLensesForUser, getAllGeneralTemplates } from 'venn-api';
import { theme } from 'venn-state';
import { useHasFF } from 'venn-utils';
import StudioPrintSettingsStore from './StudioPrintSettingsStore';
import { useForbiddenNotification } from './useForbiddenNotification';
import { usePortfoliosContext } from './usePortfoliosContext';

const NO_FACTOR_LENS_ACCESS_MESSAGE =
  'You do not have access to factor data. Contact your administrator for assistance.';

const Contexts: FC<React.PropsWithChildren<unknown>> = React.memo(function Contexts({ children }) {
  const hasContextSwitching = useHasFF('context_switching');
  const portfoliosContext = usePortfoliosContext();
  const notifyFactorLensForbidden = useForbiddenNotification(NO_FACTOR_LENS_ACCESS_MESSAGE);

  const currentTheme = useContext(ThemeContext);
  const setRecoilTheme = useSetRecoilState(theme);

  // Sync the current theme to recoil
  useEffect(() => {
    setRecoilTheme(currentTheme);
  }, [currentTheme, setRecoilTheme]);

  const setTemplates = useCallback(async () => {
    setAnalysisContextRaw((c) => ({
      ...c,
      loading: true,
    }));
    try {
      const { content: templates } = await getAllGeneralTemplates();
      setAnalysisContextRaw((c) => ({
        ...c,
        templates,
        loading: false,
      }));
    } catch {
      setAnalysisContextRaw((c) => ({
        ...c,
        templates: [],
        loading: false,
      }));
    }
  }, []);

  const setFactorLenses = useCallback(async () => {
    setFactorLensContext((c) => ({
      ...c,
      loading: true,
    }));
    try {
      const { content: factorLenses } = await getAllFactorLensesForUser();
      setFactorLensContext((c) => ({
        ...c,
        factorLenses,
        primaryFactorLens: factorLenses && factorLenses.find((factorLens) => factorLens.primary),
        loading: false,
      }));
    } catch (e: unknown) {
      setFactorLensContext((c) => ({
        ...c,
        factorLenses: undefined,
        primaryFactorLens: undefined,
        loading: false,
      }));
      notifyFactorLensForbidden(e);
    }
  }, [notifyFactorLensForbidden]);

  const [analysisContextRaw, setAnalysisContextRaw] = useState<AnalysisContextValue>({
    ...useContext(AnalysisContext),
    // Will be set later in AnalysisPage when this function will get defined
    forceRefreshAnalysis: noop,
    setForceRefreshAnalysisFunction: noop,
    refreshTemplates: setTemplates,
    loading: true,
  });

  const setForceRefreshAnalysisFunction = useCallback((forceRefreshAnalysis: () => void) => {
    setAnalysisContextRaw((prevValue) => ({
      ...prevValue,
      forceRefreshAnalysis,
    }));
  }, []);

  const analysisContext = useMemo(
    () => ({
      ...analysisContextRaw,
      setForceRefreshAnalysisFunction,
    }),
    [analysisContextRaw, setForceRefreshAnalysisFunction],
  );

  const [factorLensContext, setFactorLensContext] = useState<FactorLensesContextValue>({
    ...useContext(FactorLensesContext),
    refresh: setFactorLenses,
    loading: true,
  });

  const { currentContext, defaultContext, profileSettings } = useContext(UserContext);
  const userId = profileSettings?.user.id;
  // Context resolution is finished once we have a context, or once we know there is none to get.
  // Without the second case a user with no accessible context would wait on a context forever,
  // and never clear the loading flag that gates the whole app below.
  const contextResolved = !hasContextSwitching || currentContext !== undefined || defaultContext === undefined;

  useEffect(() => {
    if (!userId || !contextResolved) {
      return;
    }

    setTemplates();
    setFactorLenses();
  }, [userId, contextResolved, currentContext, setFactorLenses, setTemplates]);

  // Init loading
  if (factorLensContext.loading && !factorLensContext.factorLenses?.length) {
    return <Loading />;
  }

  return (
    <PortfoliosContext.Provider value={portfoliosContext}>
      <FactorLensesContext.Provider value={factorLensContext}>
        <AnalysisContext.Provider value={analysisContext}>
          <StudioPrintSettingsStore>
            <AnalysisExportStore>{children}</AnalysisExportStore>
          </StudioPrintSettingsStore>
        </AnalysisContext.Provider>
      </FactorLensesContext.Provider>
    </PortfoliosContext.Provider>
  );
});

export default Contexts;
